import { op } from "libs/transaction";
import { createAction } from "./action";
import { InvalidActionStateError } from "./utils/errors";
import { getPointer } from "libs/schema";
import { authorizeUserIsMemberOfOrganization } from "./authorizers/organizationAuthorizers";

/* -------------------------------------------------------------------------------------------------
 *  updateUserInvitation
 * -------------------------------------------------------------------------------------------------
 */

type UpdateUserInvitationProps = {
  invitationId: string;
  /** Pass "now" to set the expiration to the current server timestamp */
  expiresAt: "now" | string;
};

export const updateUserInvitation = createAction<UpdateUserInvitationProps>("invitations.updateUserInvitation", {
  operations: async (props) => {
    const expiresAt = props.expiresAt === "now" ? op.fieldValue.SERVER_TIMESTAMP() : props.expiresAt;

    return op.update(getPointer("organization_user_invitation", props.invitationId), {
      expires_at: expiresAt,
    });
  },
  authorize: async (props, context) => {
    const [invitation] = await context.environment.loader.getRecord("organization_user_invitation", props.invitationId);

    if (!invitation) {
      throw new InvalidActionStateError("[updateUserInvitation.authorize] Invitation doesn't exist");
    }

    await authorizeUserIsMemberOfOrganization({
      organizationId: invitation.organization_id,
      context,
    });
  },
});

/* -------------------------------------------------------------------------------------------------
 *  deleteUserInvitation
 * -------------------------------------------------------------------------------------------------
 */

type DeleteUserInvitationProps = {
  invitationId: string;
};

export const deleteUserInvitation = createAction<DeleteUserInvitationProps>("invitations.deleteUserInvitation", {
  operations: async (props, context) => {
    const [record] = await context.environment.loader.getRecord("organization_user_invitation", props.invitationId);

    if (!record) {
      throw new InvalidActionStateError("[deleteUserInvitation] Invitation not found", {
        errorData: { id: props.invitationId },
      });
    }

    if (!record.expires_at) {
      throw new InvalidActionStateError("[deleteUserInvitation] Only expired invitations can be deleted", {
        userMessage: "Only expired invitations can be deleted.",
        errorData: { id: props.invitationId },
      });
    }

    return op.delete(getPointer("organization_user_invitation", record));
  },
  authorize: async (props, context) => {
    const [invitation] = await context.environment.loader.getRecord("organization_user_invitation", props.invitationId);

    if (!invitation) {
      throw new InvalidActionStateError("[updateUserInvitation.authorize] Invitation doesn't exist");
    }

    await authorizeUserIsMemberOfOrganization({
      organizationId: invitation.organization_id,
      context,
    });
  },
});

export const actions = {
  updateUserInvitation,
  deleteUserInvitation,
};
